import {
  writeBrowserRuntimeReloadBreadcrumb,
} from "@/lib/browser-runtime-reload-breadcrumb";

const STALE_DEPLOYMENT_RECOVERY_STORAGE_KEY = "cord:stale-deployment-recovery";
/**
 * Limit automatic recovery to one reload attempt per URL within 30 seconds so
 * a genuine infrastructure outage does not trap the user in a reload loop.
 * If deploy propagation regularly takes longer than this, we may need a longer
 * window or a build-id-aware marker instead of a simple time-based guard.
 */
const STALE_DEPLOYMENT_RECOVERY_WINDOW_MS = 30_000;
const RECOVERABLE_DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

interface StaleDeploymentRecoveryMarker {
  attemptedAtMs: number;
  href: string;
}

interface VitePreloadErrorEvent extends Event {
  payload?: unknown;
}

let staleDeploymentRecoveryInstalled = false;
let staleDeploymentRecoveryTriggered = false;

function resolveRecoveryStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function readRecoveryMarker(storage: Storage): StaleDeploymentRecoveryMarker | null {
  try {
    const raw = storage.getItem(STALE_DEPLOYMENT_RECOVERY_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    const record = asRecord(parsed);
    if (!record) return null;

    const attemptedAtMs = record.attemptedAtMs;
    const href = record.href;
    if (typeof attemptedAtMs !== "number" || !Number.isFinite(attemptedAtMs)) {
      return null;
    }
    if (typeof href !== "string" || href.length === 0) {
      return null;
    }

    return {
      attemptedAtMs,
      href,
    };
  } catch {
    return null;
  }
}

function writeRecoveryMarker(storage: Storage, marker: StaleDeploymentRecoveryMarker): void {
  try {
    storage.setItem(STALE_DEPLOYMENT_RECOVERY_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // Best effort only — inability to persist the guard should not block recovery.
  }
}

function extractErrorMessage(error: unknown): string | null {
  if (typeof error === "string") {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  const record = asRecord(error);
  if (!record) return null;

  const message = record.message;
  return typeof message === "string" ? message : null;
}

function isRecoverableDynamicImportError(error: unknown): boolean {
  const message = extractErrorMessage(error);
  if (!message) return false;

  return RECOVERABLE_DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function shouldSkipRecoveryAttempt(args: { storage: Storage | null; href: string; nowMs: number }): boolean {
  if (staleDeploymentRecoveryTriggered) {
    return true;
  }

  if (!args.storage) {
    return false;
  }

  const marker = readRecoveryMarker(args.storage);
  if (!marker) {
    return false;
  }

  if (marker.href !== args.href) {
    return false;
  }

  return args.nowMs - marker.attemptedAtMs <= STALE_DEPLOYMENT_RECOVERY_WINDOW_MS;
}

type StaleDeploymentRecoveryAttemptResult = "reloaded" | "skipped";

function attemptStaleDeploymentRecovery(args: { source: string; error: unknown }): StaleDeploymentRecoveryAttemptResult {
  if (typeof window === "undefined") return "skipped";

  const nowMs = Date.now();
  const href = window.location.href;
  const storage = resolveRecoveryStorage();

  if (shouldSkipRecoveryAttempt({ storage, href, nowMs })) {
    console.warn("stale_deployment_recovery_skipped", {
      source: args.source,
      href,
      error: extractErrorMessage(args.error),
    });
    return "skipped";
  }

  staleDeploymentRecoveryTriggered = true;
  if (storage) {
    writeRecoveryMarker(storage, { attemptedAtMs: nowMs, href });
  }

  console.warn("stale_deployment_recovery_reload", {
    source: args.source,
    href,
    error: extractErrorMessage(args.error),
  });
  writeBrowserRuntimeReloadBreadcrumb({
    cause: "stale_deployment_recovery",
    href,
    path: window.location.pathname,
    nowMs,
  });
  window.location.reload();
  return "reloaded";
}

/**
 * When TanStack Router (or React) catches a failed lazy-route chunk load, the
 * rejection may never surface as `window.onunhandledrejection`. Call this from
 * route `errorComponent` so deploy mismatches still hard-reload like the global
 * listeners in `installStaleDeploymentRecovery`.
 */
export function tryRecoverFromStaleDeploymentChunkFailure(args: {
  source: string;
  error: unknown;
}): "reloaded" | "skipped" | "not_recoverable" {
  if (typeof window === "undefined") return "not_recoverable";
  if (import.meta.env.DEV) return "not_recoverable";
  if (!isRecoverableDynamicImportError(args.error)) return "not_recoverable";
  return attemptStaleDeploymentRecovery(args);
}

export function installStaleDeploymentRecovery(): void {
  if (typeof window === "undefined") return;
  if (import.meta.env.DEV) return;
  if (staleDeploymentRecoveryInstalled) return;

  staleDeploymentRecoveryInstalled = true;

  window.addEventListener("vite:preloadError", (event) => {
    const viteEvent = event as VitePreloadErrorEvent;
    // Do NOT call preventDefault() here. Preventing the default causes Vite's
    // preload helper to resolve with `undefined` instead of rejecting. That
    // makes lazyRouteComponent receive `undefined` as the module and throw
    // "Cannot read properties of undefined (reading 'component')" — an error
    // TanStack Router cannot recognise as a stale-chunk issue. By letting the
    // error propagate, the dynamic-import rejection flows into
    // lazyRouteComponent's .catch(), where isModuleNotFoundError matches it and
    // triggers a graceful suspend + reload with no error-boundary flash.
    void attemptStaleDeploymentRecovery({
      source: "vite:preloadError",
      error: viteEvent.payload,
    });
  });

  window.addEventListener("error", (event) => {
    if (!isRecoverableDynamicImportError(event.error ?? event.message)) {
      return;
    }

    event.preventDefault();
    void attemptStaleDeploymentRecovery({
      source: "window:error",
      error: event.error ?? event.message,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (!isRecoverableDynamicImportError(event.reason)) {
      return;
    }

    event.preventDefault();
    void attemptStaleDeploymentRecovery({
      source: "window:unhandledrejection",
      error: event.reason,
    });
  });
}
